import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  getDoc,
  updateDoc, 
  deleteDoc,
  arrayUnion, 
  arrayRemove,
  increment,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { parseMentions, validateMentions } from '../utils/mentionUtils';
import { createMentionNotification } from './notificationService';

export const createReply = async (postId, text, user, channelId) => {
  try {
    // Parse mentions from the text
    const mentionedUsernames = parseMentions(text);
    
    // Add reply to the replies subcollection
    const repliesRef = collection(db, 'posts', postId, 'replies');
    const docRef = await addDoc(repliesRef, {
      text: text.trim(),
      authorId: user.uid,
      authorEmail: user.email,
      postId: postId,
      channelId: channelId,
      createdAt: serverTimestamp(),
      likesCount: 0,
      likedByUsers: [],
      mentionedUsernames: mentionedUsernames
    });

    // Update the parent post's reply count
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      repliesCount: increment(1)
    });

    // Create notifications for mentioned users
    if (mentionedUsernames.length > 0) {
      try {
        const { validMentions } = await validateMentions(mentionedUsernames);
        
        // Create notification for each valid mentioned user
        const notificationPromises = validMentions
          .filter(mentionedUser => mentionedUser.email !== user.email) // Don't notify yourself
          .map(mentionedUser => 
            createMentionNotification(
              mentionedUser.email,
              user,
              'reply',
              docRef.id,
              channelId,
              postId // For replies, include the parent post ID
            )
          );
        
        await Promise.all(notificationPromises);
      } catch (notificationError) {
        console.error('Error creating mention notifications:', notificationError);
        // Don't fail the reply creation if notifications fail
      }
    }

    return docRef.id;
  } catch (error) {
    console.error('Error creating reply:', error);
    throw error;
  }
};

export const subscribeToReplies = (postId, callback) => {
  const repliesRef = collection(db, 'posts', postId, 'replies');
  const q = query(repliesRef, orderBy('createdAt', 'asc')); // Oldest first for replies
  
  return onSnapshot(q, (querySnapshot) => {
    const replies = [];
    querySnapshot.forEach((doc) => {
      replies.push({
        id: doc.id,
        ...doc.data()
      });
    });
    callback(replies);
  });
};

export const toggleReplyLike = async (postId, replyId, user) => {
  try {
    const replyRef = doc(db, 'posts', postId, 'replies', replyId);
    
    // Get the current reply to check if user already liked it
    const replyDoc = await getDoc(replyRef);
    if (!replyDoc.exists()) {
      throw new Error('Reply not found');
    }
    
    const replyData = replyDoc.data();
    const likedByUsers = replyData.likedByUsers || [];
    const isLiked = likedByUsers.some(likedUser => likedUser.id === user.uid);
    
    const userLikeData = {
      id: user.uid,
      email: user.email
    };
    
    if (isLiked) {
      // Unlike the reply
      const updatedLikedBy = likedByUsers.filter(likedUser => likedUser.id !== user.uid);
      await updateDoc(replyRef, {
        likedByUsers: updatedLikedBy,
        likesCount: increment(-1)
      });
    } else {
      // Like the reply
      await updateDoc(replyRef, {
        likedByUsers: arrayUnion(userLikeData),
        likesCount: increment(1)
      });
    }
    
    return !isLiked; // Return new like status
  } catch (error) {
    console.error('Error toggling reply like:', error);
    throw error;
  }
};

export const deleteReply = async (postId, replyId, userId) => {
  try {
    // First check if the user is the author of the reply
    const replyRef = doc(db, 'posts', postId, 'replies', replyId);
    const replyDoc = await getDoc(replyRef);
    
    if (!replyDoc.exists()) {
      throw new Error('Reply not found');
    }
    
    const replyData = replyDoc.data();
    if (replyData.authorId !== userId) {
      throw new Error('You can only delete your own replies');
    }
    
    // Delete the reply
    await deleteDoc(replyRef);
    
    // Update the parent post's reply count
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      repliesCount: increment(-1)
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting reply:', error);
    throw error;
  }
};

export const editReply = async (postId, replyId, newText, userId) => {
  try {
    // First check if the user is the author of the reply
    const replyRef = doc(db, 'posts', postId, 'replies', replyId);
    const replyDoc = await getDoc(replyRef);
    
    if (!replyDoc.exists()) {
      throw new Error('Reply not found');
    }
    
    const replyData = replyDoc.data();
    if (replyData.authorId !== userId) {
      throw new Error('You can only edit your own replies');
    }
    
    // Update the reply with new text and edit timestamp
    await updateDoc(replyRef, {
      text: newText.trim(),
      editedAt: serverTimestamp(),
      isEdited: true
    });
    
    return true;
  } catch (error) {
    console.error('Error editing reply:', error);
    throw error;
  }
};